/**
 * SmartInspect — Version History drawer. Mirrors the SmartEvaluate
 * VersionHistoryDrawer (VersionManager.jsx): a right-hand sheet listing the
 * live draft + immutable finalized Executive Report snapshots, with View /
 * Create New Version actions per finalized row.
 */
import { useEffect, useRef, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { History, Lock, GitBranch, Eye, FileText, Download, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  siListVersions, siCloneVersion, siVersionPdfUrl,
  siGetClientLogo, siUploadClientLogo, siDeleteClientLogo,
} from "../lib/smartInspectApi";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

async function downloadPdf(url, fallbackName) {
  const toastId = toast.loading("Generating PDF…");
  try {
    const res = await fetch(url, { credentials: "include" });
    const ctype = res.headers.get("content-type") || "";
    if (!res.ok || !ctype.includes("application/pdf")) {
      toast.error("PDF generation failed.", { id: toastId });
      return;
    }
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") || "";
    const m = /filename="?([^"]+)"?/i.exec(cd);
    const filename = m?.[1] || fallbackName;
    const objUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(objUrl);
    toast.success("Report downloaded.", { id: toastId });
  } catch (e) {
    toast.error("PDF generation failed.", { id: toastId });
  }
}

export function SmartInspectVersionHistoryDrawer({
  open, onOpenChange, assessmentId, refreshKey, onViewVersion, onVersionChanged,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [confirmCloneVid, setConfirmCloneVid] = useState(null);

  useEffect(() => {
    if (!open || !assessmentId) return;
    setLoading(true); setErr("");
    siListVersions(assessmentId)
      .then(setData)
      .catch(() => setErr("Failed to load versions"))
      .finally(() => setLoading(false));
  }, [open, assessmentId, refreshKey]);

  const handleOpenDraft = () => {
    onOpenChange(false);
    onVersionChanged?.();
  };

  const handleClone = async (vid) => {
    try {
      await siCloneVersion(assessmentId, vid);
      setConfirmCloneVid(null);
      onOpenChange(false);
      onVersionChanged?.();
    } catch {
      setErr("Failed to create new version");
      toast.error("Failed to create new version");
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[420px] sm:max-w-[420px] overflow-y-auto" data-testid="si-version-history-drawer">
          <SheetHeader className="border-b border-[#E5E7EB] pb-3 mb-4">
            <SheetTitle className="text-[15px] font-semibold text-[#111827] flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
              <History className="w-4 h-4 text-[#025979]" />
              Version History
            </SheetTitle>
            <SheetDescription className="text-xs text-[#6B7280]">
              Finalized snapshots are immutable. The active draft captures all in-progress edits.
            </SheetDescription>
          </SheetHeader>

          <ClientLogoSection assessmentId={assessmentId} refreshKey={refreshKey} />

          {loading && <p className="text-xs text-[#9CA3AF]" data-testid="si-versions-loading">Loading versions…</p>}
          {err && <p className="text-xs text-red-600" data-testid="si-versions-error">{err}</p>}

          {data && (
            <ul className="space-y-3" data-testid="si-versions-list">
              {data.draft && (
                <li className="border border-[#E5E7EB] rounded-md p-3 bg-[#FCFCFD]" data-testid={`si-version-row-draft-v${data.draft.version_number}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[13px] font-semibold text-[#111827]">v{data.draft.version_number}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">Draft</span>
                  </div>
                  <p className="text-[11px] text-[#6B7280]">Last updated: {formatDate(data.draft.updated_at)}</p>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" className="text-[11px] h-7 px-2 border-[#E5E7EB]" onClick={handleOpenDraft} data-testid="si-open-draft-btn">
                      <FileText className="w-3 h-3 mr-1" /> Open Draft
                    </Button>
                  </div>
                </li>
              )}

              {data.versions.map((v) => (
                <li key={v.id} className="border border-[#E5E7EB] rounded-md p-3 bg-white" data-testid={`si-version-row-v${v.version_number}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[13px] font-semibold text-[#111827]">v{v.version_number}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E6F2F5] text-[#025979] font-medium inline-flex items-center gap-1">
                      <Lock className="w-2.5 h-2.5" /> Finalized
                    </span>
                  </div>
                  <p className="text-[11px] text-[#6B7280]">Finalized on {formatDate(v.created_at)}</p>
                  {v.created_by?.email && <p className="text-[11px] text-[#9CA3AF]">by {v.created_by.email}</p>}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className="text-[11px] h-7 px-2 border-[#E5E7EB]"
                      onClick={() => { onOpenChange(false); onViewVersion?.(v.id); }} data-testid={`si-view-version-btn-${v.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                    <Button size="sm" variant="outline" className="text-[11px] h-7 px-2 border-[#E5E7EB]"
                      onClick={() => downloadPdf(siVersionPdfUrl(assessmentId, v.id), `SmartInspect_Report_v${v.version_number}.pdf`)}
                      data-testid={`si-pdf-version-btn-${v.id}`}>
                      <Download className="w-3 h-3 mr-1" /> PDF
                    </Button>
                    <Button size="sm" variant="outline" className="text-[11px] h-7 px-2 border-[#E5E7EB] text-[#025979]"
                      onClick={() => setConfirmCloneVid(v.id)} data-testid={`si-clone-version-btn-${v.id}`}>
                      <GitBranch className="w-3 h-3 mr-1" /> Create New Version
                    </Button>
                  </div>
                </li>
              ))}

              {!data.draft && data.versions.length === 0 && (
                <li className="text-xs text-[#9CA3AF]" data-testid="si-versions-empty">No versions yet.</li>
              )}
            </ul>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!confirmCloneVid} onOpenChange={(v) => !v && setConfirmCloneVid(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="si-clone-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-[15px]" style={{ fontFamily: "Chivo" }}>Create a new version from this snapshot?</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280] pt-1 leading-relaxed">
              The selected finalized version will be restored as the new editable draft. Any unsaved changes in the current draft will be overwritten.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setConfirmCloneVid(null)} data-testid="si-clone-cancel-btn">Cancel</Button>
            <Button size="sm" className="text-xs bg-[#025979] hover:bg-[#024a63] text-white" onClick={() => handleClone(confirmCloneVid)} data-testid="si-clone-confirm-btn">
              Restore & Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SmartInspectVersionHistoryDrawer;

// Per-assessment client logo widget (mirrors SmartEvaluate). Uploaded logo
// surfaces on the SmartInspect Executive Report PDF header.
function ClientLogoSection({ assessmentId, refreshKey }) {
  const [logo, setLogo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    if (!assessmentId) return;
    siGetClientLogo(assessmentId).then(setLogo).catch(() => setLogo(null));
  };
  useEffect(load, [assessmentId, refreshKey]);

  const onPick = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/image\/(png|jpe?g)/i.test(file.type)) { setErr("Only PNG or JPG files are supported"); return; }
    if (file.size > 2 * 1024 * 1024) { setErr("Logo must be under 2 MB"); return; }
    setErr(""); setBusy(true);
    try { await siUploadClientLogo(assessmentId, file); load(); }
    catch { setErr("Upload failed"); }
    finally { setBusy(false); }
  };
  const onDelete = async () => {
    setBusy(true);
    try { await siDeleteClientLogo(assessmentId); load(); }
    catch { setErr("Delete failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 border border-[#E5E7EB] rounded-md p-3 bg-[#FCFCFD]" data-testid="si-client-logo-section">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold text-[#374151] uppercase tracking-wider">Client Logo</span>
        <span className="text-[10px] text-[#9CA3AF]">PNG / JPG · ≤ 2 MB</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-16 h-10 rounded-sm border border-[#E5E7EB] bg-white flex items-center justify-center overflow-hidden" data-testid="si-client-logo-preview">
          {logo?.has_logo ? (
            <img loading="lazy" src={logo.data_url} alt="Client logo" className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-[9px] text-[#9CA3AF]">No logo</span>
          )}
        </div>
        <div className="flex-1 flex flex-wrap gap-1.5">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onFile} data-testid="si-client-logo-input" />
          <Button size="sm" variant="outline" className="text-[11px] h-7 px-2 border-[#E5E7EB]" onClick={onPick} disabled={busy} data-testid="si-client-logo-upload-btn">
            <ImagePlus className="w-3 h-3 mr-1" /> {logo?.has_logo ? "Replace" : "Upload"}
          </Button>
          {logo?.has_logo && (
            <Button size="sm" variant="outline" className="text-[11px] h-7 px-2 border-[#E5E7EB] text-red-600" onClick={onDelete} disabled={busy} data-testid="si-client-logo-delete-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Remove
            </Button>
          )}
        </div>
      </div>
      {err && <p className="mt-2 text-[10px] text-red-600" data-testid="si-client-logo-error">{err}</p>}
    </div>
  );
}
